import { FC, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAuth0 } from '@auth0/auth0-react';
import { useCustomer } from '../contexts/CustomerContext';
import { getUserCustomerAccess, hasPermission, PERMISSIONS } from '../../_metronic/helpers/UserRoleHelpers';
import { migrationApi } from '../pages/platform-migration/api';
import { MigrationStalled, MigrationV2 } from '../pages/platform-migration/types';

// Esperas em que a equipe devolveu a vez ao lojista (o e-mail sai no mesmo momento)
const ALERT_REASONS = ['shopify_install', 'storefront_review'];
const OPEN_STATUSES = ['draft', 'validating', 'mapping', 'ready'];
const MIGRATION_PATH = '/platform-migration';

// Cada lembrete de etapa parada tem o próprio número: um novo volta a aparecer depois de visto
const alertIdOf = (migration: MigrationV2 | null, stalled: MigrationStalled | null) => {
  if (!migration || !OPEN_STATUSES.includes(migration.status)) return null;
  const reason = migration.waitingOn?.party === 'customer' ? migration.waitingOn.reason : undefined;
  if (reason && ALERT_REASONS.includes(reason)) return reason;
  return stalled ? `stalled-${stalled.count}` : null;
};

const seenKey = (migration: MigrationV2, alertId: string) => `migration-alert-seen:${migration._id}:${alertId}`;

const wasSeen = (migration: MigrationV2, alertId: string) => {
  try {
    return window.localStorage.getItem(seenKey(migration, alertId)) === '1';
  } catch {
    return false;
  }
};

const markSeen = (migration: MigrationV2, alertId: string) => {
  try {
    window.localStorage.setItem(seenKey(migration, alertId), '1');
  } catch {
    // sem armazenamento: o aviso volta na próxima carga, o que é aceitável
  }
};

/**
 * Aviso no topo do painel quando a migração de plataforma volta para o lojista (app da Shopify
 * criado pela equipe, front da loja nova pronto no modo "Konfidency faz") ou quando a etapa dele
 * está parada (lembrete do flow stalled-step-reminder). Sai quando ele abre a migração ou
 * dispensa; o estado vem do servidor, o mesmo que dispara o e-mail.
 */
const MigrationPendingAlert: FC = () => {
  const intl = useIntl();
  const { user } = useAuth0();
  const { customer } = useCustomer();
  const location = useLocation();
  const [migration, setMigration] = useState<MigrationV2 | null>(null);
  const [stalled, setStalled] = useState<MigrationStalled | null>(null);
  const [seen, setSeen] = useState(false);

  const key = customer?.key;
  const onMigrationRoute = location.pathname.startsWith(MIGRATION_PATH);
  // A equipe acompanha pelo Asgard; o endpoint exige platform:migrate
  const canMigrate = !!user && !!key && !getUserCustomerAccess(user).isKonfidencyAdmin && hasPermission(user, key, PERMISSIONS.PLATFORM_MIGRATE);

  // Uma consulta por cliente, refeita ao entrar e ao sair da migração (o lojista pode ter resolvido lá;
  // quem chega pelo link do e-mail entra direto nela e o aviso conta como visto)
  useEffect(() => {
    setMigration(null);
    if (!canMigrate) return;
    let cancelled = false;
    migrationApi
      .getActive()
      .then((result) => {
        if (cancelled) return;
        const alertId = alertIdOf(result.migration, result.stalled);
        setMigration(result.migration);
        setStalled(result.stalled);
        setSeen(!!result.migration && !!alertId && wasSeen(result.migration, alertId));
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [key, canMigrate, onMigrationRoute]);

  const alertId = alertIdOf(migration, stalled);

  // Abrir a migração conta como visto
  useEffect(() => {
    if (!alertId || !onMigrationRoute || !migration) return;
    markSeen(migration, alertId);
    setSeen(true);
  }, [alertId, onMigrationRoute, migration]);

  if (!canMigrate || !alertId || !migration || seen || onMigrationRoute) return null;

  const dismiss = () => {
    markSeen(migration, alertId);
    setSeen(true);
  };

  let icon = 'bi-shop';
  let tone = 'primary';
  let title: string;
  let body: string;
  if (alertId === 'shopify_install') {
    icon = 'bi-bag-check';
    title = intl.formatMessage({ id: 'platform-migration.alert.shopify-install.title', defaultMessage: 'O app da Shopify da sua loja nova está pronto' });
    body = intl.formatMessage({
      id: 'platform-migration.alert.shopify-install.body',
      defaultMessage: 'Nossa equipe criou o app. Instale na Shopify para conectar a loja nova à migração.',
    });
  } else if (alertId === 'storefront_review') {
    title = intl.formatMessage({ id: 'platform-migration.alert.storefront-review.title', defaultMessage: 'A Konfidency já está na sua loja nova' });
    body = intl.formatMessage({
      id: 'platform-migration.alert.storefront-review.body',
      defaultMessage: 'O script e os widgets da Konfidency já aparecem na loja nova. Confira como ficou e faça a virada quando a loja nova entrar no ar.',
    });
  } else {
    icon = 'bi-hourglass-split';
    tone = 'warning';
    const step = intl.formatMessage({ id: `platform-migration.step.${stalled?.step}`, defaultMessage: stalled?.step || '' });
    title = intl.formatMessage(
      { id: 'platform-migration.alert.stalled.title', defaultMessage: 'Sua migração de plataforma está parada há {days} dias' },
      { days: stalled?.days }
    );
    body = intl.formatMessage(
      {
        id: 'platform-migration.alert.stalled.body',
        defaultMessage: 'Ela está na etapa "{step}". Continue de onde parou para não atrasar a troca de loja.',
      },
      { step }
    );
  }

  return (
    <div className={`alert alert-${tone} d-flex align-items-center p-5 mb-8`}>
      <i className={`bi ${icon} fs-2x text-${tone} me-4`}></i>
      <div className='d-flex flex-column flex-grow-1'>
        <h4 className='mb-1 text-gray-900'>{title}</h4>
        <span className='text-gray-700'>{body}</span>
      </div>
      <Link to={MIGRATION_PATH} className={`btn btn-${tone} ms-4 text-nowrap`} onClick={dismiss}>
        {intl.formatMessage({ id: 'platform-migration.alert.cta', defaultMessage: 'Abrir a migração' })}
      </Link>
      <button
        type='button'
        className={`btn btn-icon btn-sm btn-active-light-${tone} ms-2`}
        onClick={dismiss}
        aria-label={intl.formatMessage({ id: 'platform-migration.alert.dismiss', defaultMessage: 'Dispensar aviso' })}
      >
        <i className='bi bi-x-lg fs-4'></i>
      </button>
    </div>
  );
};

export default MigrationPendingAlert;
